/**
 * Utility functions to format IDs in a more user-friendly way
 */

/**
 * Convert a long UUID or ID into a shorter, more friendly format
 * @param {string|number} id - The original ID (UUID or other)
 * @param {string} prefix - Optional prefix (e.g., 'JOB', 'CAND', 'SUB')
 * @param {number} length - Length of the shortened ID (default: 6)
 * @returns {string} - Formatted friendly ID
 */
export const formatFriendlyId = (id, prefix = '', length = 6) => {
  if (!id) return 'N/A';
  
  // Convert to string if it's not already
  const idStr = String(id);
  
  // If it's already a short, friendly ID, return as-is
  if (idStr.length <= 10 && !idStr.includes('-')) {
    return prefix ? `${prefix}-${idStr}` : idStr;
  }
  
  // For UUIDs or long IDs, take first part and make it friendly
  const cleanId = idStr.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const shortId = cleanId.substring(0, length);
  
  return prefix ? `${prefix}-${shortId}` : `#${shortId}`;
};

/**
 * Generate friendly IDs for different entity types
 */
export const formatJobId = (id) => formatFriendlyId(id, 'JOB', 6);
export const formatCandidateId = (id) => formatFriendlyId(id, 'CAND', 5);
export const formatSubmissionId = (id) => formatFriendlyId(id, 'SUB', 6);
export const formatRequestId = (id) => formatFriendlyId(id, 'REQ', 5);

/**
 * Format ID with copy functionality
 * @param {string} id - The original ID
 * @param {string} friendlyId - The friendly formatted ID
 * @returns {object} - Object with friendly display and copy functionality
 */
export const createCopyableId = (id, friendlyId) => ({
  display: friendlyId,
  full: id,
  copyToClipboard: () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(id);
      return true;
    }
    return false;
  }
});

/**
 * Generate props for a friendly ID component (returns object with display text and handlers)
 */
export const createFriendlyIdProps = (id, type = 'generic', className = '') => {
  let formattedId;
  
  switch (type) {
    case 'job':
      formattedId = formatJobId(id);
      break;
    case 'candidate':
      formattedId = formatCandidateId(id);
      break;
    case 'submission':
      formattedId = formatSubmissionId(id);
      break;
    case 'request':
      formattedId = formatRequestId(id);
      break;
    default:
      formattedId = formatFriendlyId(id);
  }

  const handleCopy = () => {
    if (navigator.clipboard && id) {
      navigator.clipboard.writeText(id);
    }
  };

  return {
    displayText: formattedId,
    fullId: id,
    onClick: handleCopy,
    title: `Full ID: ${id} (click to copy)`,
    className: `inline-flex items-center space-x-1 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors ${className}`
  };
};

export default {
  formatFriendlyId,
  formatJobId,
  formatCandidateId,
  formatSubmissionId,
  formatRequestId,
  createCopyableId,
  createFriendlyIdProps
};